import {
  axialToWorld,
  centerSpacingPx,
  getMapAxialBounds,
  hexKey,
  isPointInMap,
  parseHexKey,
} from './hexGridMath';
import { networkEdgeKey } from './mapFeatures';

const NEIGHBORS = [
  [1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1],
];

const UNKNOWN_TERRAIN = {
  id: 'unknown',
  movementCost: 1.35,
  dangerCost: 1.15,
  exposureCost: 1,
  tags: [],
};

class MinHeap {
  constructor() {
    this.items = [];
  }

  push(item) {
    this.items.push(item);
    let index = this.items.length - 1;
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      if (this.items[parent].priority <= item.priority) break;
      this.items[index] = this.items[parent];
      index = parent;
    }
    this.items[index] = item;
  }

  pop() {
    if (!this.items.length) return null;
    const root = this.items[0];
    const tail = this.items.pop();
    if (!this.items.length) return root;
    let index = 0;
    while (true) {
      const left = index * 2 + 1;
      const right = left + 1;
      if (left >= this.items.length) break;
      let child = left;
      if (right < this.items.length && this.items[right].priority < this.items[left].priority) child = right;
      if (this.items[child].priority >= tail.priority) break;
      this.items[index] = this.items[child];
      index = child;
    }
    this.items[index] = tail;
    return root;
  }

  get size() {
    return this.items.length;
  }
}

const valueAtPath = (context, path) => path.split('.').reduce(
  (value, part) => value == null ? undefined : value[part],
  context,
);

export const evaluateExpression = (expression, context) => {
  if (!expression) return undefined;
  if (expression.op === 'const') return expression.value;
  if (expression.op === 'var') return valueAtPath(context, expression.path);
  const args = expression.args.map((arg) => evaluateExpression(arg, context));
  switch (expression.op) {
    case 'all': return args.every(Boolean);
    case 'any': return args.some(Boolean);
    case 'not': return !args[0];
    case 'eq': return args[0] === args[1];
    case 'ne': return args[0] !== args[1];
    case 'gt': return args[0] > args[1];
    case 'gte': return args[0] >= args[1];
    case 'lt': return args[0] < args[1];
    case 'lte': return args[0] <= args[1];
    case 'in': return Array.isArray(args[1]) ? args[1].includes(args[0]) : false;
    case 'contains': return Array.isArray(args[0]) || typeof args[0] === 'string' ? args[0].includes(args[1]) : false;
    case 'add': return args.reduce((total, value) => total + Number(value || 0), 0);
    case 'multiply': return args.reduce((total, value) => total * Number(value), 1);
    case 'min': return Math.min(...args.map(Number));
    case 'max': return Math.max(...args.map(Number));
    default: return undefined;
  }
};

const transportAssessment = (terrain, transport, pace, dangerLevel) => {
  const context = { terrain, transport, pace, danger: dangerLevel };
  let traversable = transport.defaultTraversable !== false;
  let multiplier = 1;
  (transport.movementRules || []).forEach((rule) => {
    if (!evaluateExpression(rule.when, context)) return;
    traversable = Boolean(evaluateExpression(rule.traversable, context));
    const ruleMultiplier = Number(evaluateExpression(rule.costMultiplier, context));
    if (Number.isFinite(ruleMultiplier) && ruleMultiplier > 0) multiplier *= ruleMultiplier;
  });
  return { traversable, multiplier };
};

export const hexDistance = (first, second) => (
  (Math.abs(first.q - second.q)
    + Math.abs(first.q + first.r - second.q - second.r)
    + Math.abs(first.r - second.r)) / 2
);

const reconstructPath = (cameFrom, destinationKey) => {
  const path = [destinationKey];
  while (cameFrom.has(path[0])) path.unshift(cameFrom.get(path[0]));
  return path;
};

export const assessRouteCell = ({
  fromKey,
  key,
  terrainById,
  assignments,
  infrastructureById,
  connectionById,
  conditionById,
  networkSegments,
  conditionAssignments,
  transport,
  pace,
  dangerLevel,
}) => {
  const assignment = assignments[key];
  const terrain = terrainById[assignment?.terrainId] || UNKNOWN_TERRAIN;
  const segment = fromKey ? networkSegments[networkEdgeKey(fromKey, key)] : null;
  const infrastructure = segment?.catalog === 'infrastructure'
    ? infrastructureById[segment.typeId]
    : null;
  const connection = segment?.catalog === 'connection'
    ? connectionById[segment.typeId]
    : null;
  const conditions = (conditionAssignments[key] || [])
    .map((conditionId) => conditionById[conditionId])
    .filter(Boolean);
  const infrastructureAvailable = Boolean(infrastructure) && (
    !infrastructure.transportTags?.length
    || infrastructure.transportTags.some((tag) => transport.tags?.includes(tag))
  );
  const effectiveInfrastructure = infrastructureAvailable ? infrastructure : null;
  const effectiveTerrain = {
    ...terrain,
    tags: [
      ...(terrain.tags || []),
      ...(effectiveInfrastructure?.tags || []),
      ...conditions.flatMap((condition) => condition.tags || []),
    ],
  };
  const transportResult = transportAssessment(effectiveTerrain, transport, pace, dangerLevel);
  const connectionAvailable = Boolean(connection?.transportIds?.includes(transport.id));
  const blockedByCondition = conditions.some((condition) => (
    (condition.blocksTransportTags || []).some((tag) => transport.tags?.includes(tag))
  ));
  const policy = transport.networkPolicy || { mode: 'free' };
  const onPreferredNetwork = Boolean(
    (effectiveInfrastructure && policy.infrastructureIds?.includes(segment.typeId))
    || (connectionAvailable && policy.connectionIds?.includes(segment.typeId))
  );
  const policyTraversable = policy.mode !== 'required' || onPreferredNetwork;
  const offNetworkMultiplier = policy.mode === 'preferred' && !onPreferredNetwork
    ? Number(policy.offNetworkMultiplier || 1)
    : 1;
  const featureMultiplier = (effectiveInfrastructure?.movementMultiplier || 1)
    * (connectionAvailable ? connection.costMultiplier : 1)
    * conditions.reduce((total, condition) => total * (condition.movementMultiplier || 1), 1)
    * offNetworkMultiplier;
  return {
    terrain: effectiveTerrain,
    terrainId: terrain.id,
    networkCatalog: segment?.catalog || null,
    networkTypeId: segment?.typeId || null,
    onPreferredNetwork,
    unknown: !assignment,
    traversable: !blockedByCondition && policyTraversable
      && (transportResult.traversable || connectionAvailable),
    movementFactor: terrain.movementCost * transportResult.multiplier * featureMultiplier,
    dangerCost: terrain.dangerCost ?? 1,
    exposureCost: terrain.exposureCost ?? 1,
  };
};

const objectiveCost = (cell, profile, pace, overlapCount = 0) => {
  const base = profile.durationWeight * cell.movementFactor
    + profile.dangerWeight * cell.dangerCost
    + profile.exposureWeight * cell.exposureCost * pace.exposureMultiplier;
  return base + overlapCount * Math.max(0.5, base * 0.55);
};

const findSegment = ({
  startKey,
  destinationKey,
  grid,
  width,
  height,
  terrainById,
  assignments,
  infrastructureById,
  connectionById,
  conditionById,
  networkSegments,
  conditionAssignments,
  avoidedHexKeys,
  transport,
  pace,
  dangerLevel,
  profile,
  overlapCounts,
}) => {
  if (startKey === destinationKey) return [startKey];
  const start = parseHexKey(startKey);
  const destination = parseHexKey(destinationKey);
  const bounds = getMapAxialBounds(grid, width, height);
  const open = new MinHeap();
  const cameFrom = new Map();
  const bestCost = new Map([[startKey, 0]]);
  // Network and condition multipliers are package data and may be arbitrarily
  // small. Dijkstra's zero heuristic stays correct for every valid package.
  open.push({ key: startKey, axial: start, cost: 0, priority: 0 });
  let expanded = 0;

  while (open.size && expanded < 100000) {
    const current = open.pop();
    if (current.cost !== bestCost.get(current.key)) continue;
    if (current.key === destinationKey) return reconstructPath(cameFrom, destinationKey);
    expanded += 1;

    NEIGHBORS.forEach(([dq, dr]) => {
      const axial = { q: current.axial.q + dq, r: current.axial.r + dr };
      if (axial.q < bounds.qMin - 1 || axial.q > bounds.qMax + 1 || axial.r < bounds.rMin - 1 || axial.r > bounds.rMax + 1) return;
      if (!isPointInMap(axialToWorld(axial.q, axial.r, grid), width, height)) return;
      const key = hexKey(axial.q, axial.r);
      if (avoidedHexKeys.has(key) && key !== destinationKey && key !== startKey) return;
      const cell = assessRouteCell({
        fromKey: current.key,
        key,
        terrainById,
        assignments,
        infrastructureById,
        connectionById,
        conditionById,
        networkSegments,
        conditionAssignments,
        transport,
        pace,
        dangerLevel,
      });
      if (!cell.traversable && key !== startKey) return;
      const nextCost = current.cost + objectiveCost(cell, profile, pace, overlapCounts.get(key) || 0);
      if (nextCost >= (bestCost.get(key) ?? Number.POSITIVE_INFINITY)) return;
      cameFrom.set(key, current.key);
      bestCost.set(key, nextCost);
      open.push({
        key,
        axial,
        cost: nextCost,
        priority: nextCost,
      });
    });
  }
  return null;
};

const routeMetrics = ({
  path,
  grid,
  metersPerPixel,
  terrainById,
  assignments,
  infrastructureById,
  connectionById,
  conditionById,
  networkSegments,
  conditionAssignments,
  transport,
  pace,
  dangerLevel,
}) => {
  const stepDistance = centerSpacingPx(grid) * metersPerPixel;
  let durationHours = 0;
  let dangerTotal = 0;
  let exposureTotal = 0;
  const unknown = new Set();
  path.slice(1).forEach((key, index) => {
    const cell = assessRouteCell({
      fromKey: path[index],
      key,
      terrainById,
      assignments,
      infrastructureById,
      connectionById,
      conditionById,
      networkSegments,
      conditionAssignments,
      transport,
      pace,
      dangerLevel,
    });
    durationHours += stepDistance * cell.movementFactor / (transport.baseSpeedMetersPerHour * pace.speedMultiplier);
    dangerTotal += cell.dangerCost;
    exposureTotal += cell.exposureCost;
    if (cell.unknown) unknown.add(key);
  });
  const steps = Math.max(1, path.length - 1);
  return {
    distanceMeters: stepDistance * (path.length - 1),
    durationHours,
    dangerIndex: Math.min(100, Math.round(dangerTotal / steps * dangerLevel.eventMultiplier * 35)),
    exposureIndex: Math.min(100, Math.round(exposureTotal / steps * pace.exposureMultiplier * 40)),
    unknownHexKeys: [...unknown],
  };
};

export const assessRouteLegs = ({
  path = [],
  grid,
  metersPerPixel,
  terrains = [],
  assignments = {},
  infrastructure = [],
  connections = [],
  conditions = [],
  networkSegments = {},
  conditionAssignments = {},
  transport,
  pace,
  dangerLevel,
}) => {
  if (!transport || !pace || !dangerLevel || path.length < 2 || !metersPerPixel) return [];
  const terrainById = Object.fromEntries(terrains.map((terrain) => [terrain.id, terrain]));
  const infrastructureById = Object.fromEntries(infrastructure.map((item) => [item.id, item]));
  const connectionById = Object.fromEntries(connections.map((item) => [item.id, item]));
  const conditionById = Object.fromEntries(conditions.map((item) => [item.id, item]));
  const distanceMeters = centerSpacingPx(grid) * metersPerPixel;
  return path.slice(1).map((key, index) => {
    const fromKey = path[index];
    const cell = assessRouteCell({
      fromKey,
      key,
      terrainById,
      assignments,
      infrastructureById,
      connectionById,
      conditionById,
      networkSegments,
      conditionAssignments,
      transport,
      pace,
      dangerLevel,
    });
    return {
      fromKey,
      toKey: key,
      distanceMeters,
      durationHours: distanceMeters * cell.movementFactor
        / (transport.baseSpeedMetersPerHour * pace.speedMultiplier),
      ...cell,
    };
  });
};

export const planRouteVariants = ({
  startKey,
  destinationKey,
  waypointKeys = [],
  grid,
  width,
  height,
  metersPerPixel,
  terrains,
  assignments,
  infrastructure = [],
  connections = [],
  conditions = [],
  networkSegments = {},
  conditionAssignments = {},
  avoidedHexKeys = [],
  transport,
  pace,
  dangerLevel,
  profiles,
}) => {
  if (!startKey || !destinationKey || !metersPerPixel || !transport || !pace || !dangerLevel) return [];
  const terrainById = Object.fromEntries(terrains.map((terrain) => [terrain.id, terrain]));
  const infrastructureById = Object.fromEntries(infrastructure.map((item) => [item.id, item]));
  const connectionById = Object.fromEntries(connections.map((item) => [item.id, item]));
  const conditionById = Object.fromEntries(conditions.map((item) => [item.id, item]));
  const avoided = new Set(avoidedHexKeys);
  const overlapCounts = new Map();
  const stops = [startKey, ...waypointKeys, destinationKey];

  return profiles.map((profile) => {
    const path = [];
    for (let index = 0; index < stops.length - 1; index += 1) {
      const segment = findSegment({
        startKey: stops[index],
        destinationKey: stops[index + 1],
        grid,
        width,
        height,
        terrainById,
        assignments,
        infrastructureById,
        connectionById,
        conditionById,
        networkSegments,
        conditionAssignments,
        avoidedHexKeys: avoided,
        transport,
        pace,
        dangerLevel,
        profile,
        overlapCounts,
      });
      if (!segment) return { profileId: profile.id, path: [], unavailable: true };
      path.push(...(index === 0 ? segment : segment.slice(1)));
    }
    path.slice(1, -1).forEach((key) => overlapCounts.set(key, (overlapCounts.get(key) || 0) + 1));
    return {
      profileId: profile.id,
      path,
      unavailable: false,
      ...routeMetrics({
        path,
        grid,
        metersPerPixel,
        terrainById,
        assignments,
        infrastructureById,
        connectionById,
        conditionById,
        networkSegments,
        conditionAssignments,
        transport,
        pace,
        dangerLevel,
      }),
    };
  });
};
